/**
 * One choice from a short list, as native radio buttons in a named group. A screen reader hears the
 * group's name, each option, and which one is chosen.
 *
 * `list` is a column of radios with labels. `cards` makes each option a large target with a
 * description, for choices a person reads before picking, such as a health question or a protein.
 *
 * The arrow keys move through the options and choose as they go, as every radio group does. With
 * `manual` they only move, and Space or Enter chooses: for a group where choosing does something at
 * once, such as a question that moves on as soon as it is answered. There, choosing the option
 * already chosen counts as choosing it again.
 *
 * The group is named by its visible `label`, or by a heading already on the page through
 * `labelledBy`. An `error` sits under the options and is the group's description.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { choiceStyle, moveWithoutChoosing } from "./choice.ts";
import { focusRing } from "./focus.ts";
import { Spinner } from "./Spinner.tsx";
import { visuallyHidden } from "./visuallyHidden.ts";
import { WarningIcon } from "./icons.tsx";
import { textStyle } from "./textStyle.ts";
const styles = {
  legend: {
    kmVPX3: "x1717udv",
    k1K539: "xz4076n",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  hint: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  options: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1173whf",
    $$css: true
  },
  horizontal: {
    kXwgrk: "x1q0g3np",
    kwnvtZ: "x1a02dak",
    k1C7PZ: "x1vdzsm",
    $$css: true
  },
  cardsHorizontal: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x1i3q30n",
    $$css: true
  },
  row: {
    k8WAf4: "xpdwd9g x93fw87",
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x1173whf",
    kkrTdU: "x1ypdohk",
    kMwMTN: "xm8xz4y",
    kHjlTd: "xj0a0fe",
    $$css: true
  },
  rowDisabled: {
    kkrTdU: "x1h6gzvc",
    kMwMTN: "x16nvyyk",
    $$css: true
  },
  dot: {
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kaIpWk: "x1m3fsnl",
    $$css: true
  },
  dotOn: {
    kWkggS: "xmbr0q7",
    kVAM5u: "xtfhg7g",
    kGVxlE: "xbo5ooc",
    $$css: true
  },
  description: {
    kZCmMZ: "x15lra4c",
    kMwMTN: "x1lm7cez",
    k63SB2: "xox936i",
    $$css: true
  },
  cardInput: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    kaIpWk: "x1fbwzx",
    kWkggS: "x40k5rp",
    kVAM5u: "x1j7zvbq xbqcxop",
    $$css: true
  },
  cardInputOn: {
    kWkggS: "x3oldu3",
    kVAM5u: "xtfhg7g",
    kMzoRj: "x4lnxqg",
    $$css: true
  },
  cardInputDisabled: {
    kWkggS: "x14ba300",
    kVAM5u: "x17k0sde",
    $$css: true
  },
  cardDot: {
    kVAEAm: "x1n2onr6",
    kzqmXN: "x1z0q0p5",
    kZKoxP: "xq9ob8z",
    kqGvvJ: "x1h3jhxu",
    kB7OPa: "x9f619",
    kaIpWk: "x1m3fsnl",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xdmng3o",
    kWkggS: "x40k5rp",
    kfzvcC: "x47corl",
    $$css: true
  },
  cardLabel: {
    kMwMTN: "xm8xz4y",
    k63SB2: "xqu410z",
    $$css: true
  },
  cardDescription: {
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  disabledText: {
    kMwMTN: "x16nvyyk",
    $$css: true
  },
  error: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "xskm3c9",
    kogj98: "x1ghz6dp",
    kMwMTN: "x6ygkrl",
    $$css: true
  }
};
export interface RadioOption<Value extends string> {
  readonly value: Value;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  /** Why this option cannot be chosen. Shown in place of the description while it is disabled. */
  readonly disabledReason?: string;
  readonly testId?: string;
}
interface Shared<Value extends string> {
  readonly options: readonly RadioOption<Value>[];
  /** The chosen option, or null before anything is chosen. */
  readonly value: Value | null;
  readonly onChange: (value: Value) => void;
  readonly variant?: "list" | "cards";
  readonly orientation?: "vertical" | "horizontal";
  /** Arrow keys move without choosing. See the note at the top of this file. */
  readonly manual?: boolean;
  /**
   * While a choice saves: the group says it is busy and ignores presses, and keeps focus where it
   * is, which `disabled` would not.
   */
  readonly loading?: boolean;
  readonly hint?: ReactNode;
  /** Adds "(optional)" to the label, for a choice a person may leave unanswered. */
  readonly optional?: boolean;
  /** The id of text elsewhere on the page that describes the choice, such as what is missing. */
  readonly describedBy?: string | undefined;
  readonly error?: string | null;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly testId?: string;
}

/** Named by a visible legend, or by the id of a heading that already names it. */
export type RadioGroupProps<Value extends string> = Shared<Value> & ({
  readonly label: string;
  readonly labelledBy?: never;
} | {
  readonly labelledBy: string;
  readonly label?: never;
});
export function RadioGroup<Value extends string>(props: RadioGroupProps<Value>) {
  const made = useId();
  const name = props.name ?? made;
  const legendId = `${made}-legend`;
  const hintId = `${made}-hint`;
  const errorId = `${made}-error`;
  const failed = props.error !== undefined && props.error !== null && props.error !== "";
  const cards = props.variant === "cards";
  const horizontal = props.orientation === "horizontal";

  /*
    The option just chosen, shown as chosen with a spinner while the page saves it, so the press
    shows at once rather than when the server answers.
  */
  const [asked, setAsked] = useState<Value | null>(null);
  const saving = props.loading === true;
  useEffect(() => {
    if (!saving) setAsked(null);
  }, [saving]);
  const pick = (value: Value) => {
    if (saving) return;
    setAsked(value);
    props.onChange(value);
  };
  const chosen = saving && asked !== null ? asked : props.value;
  const describedBy = [props.describedBy ?? null, props.hint === undefined ? null : hintId, failed ? errorId : null].filter(part => part !== null).join(" ");
  return <>
      <fieldset role="radiogroup" aria-labelledby={props.labelledBy ?? legendId} aria-describedby={describedBy === "" ? undefined : describedBy} aria-invalid={failed || undefined} aria-busy={props.loading === true || undefined} data-testid={props.testId} className="x78zum5 xdt5ytf x1173whf xeuugli x1ghz6dp x1717udv xc342km">
        {props.label !== undefined && <legend id={legendId} {...stylex.props(textStyle.label, styles.legend)}>
            {props.label}
            {props.optional === true && <span className="x1lm7cez"> (optional)</span>}
          </legend>}
        {props.hint !== undefined && <p id={hintId} {...stylex.props(textStyle.caption, styles.hint)}>
            {props.hint}
          </p>}
        <div {...stylex.props(styles.options, horizontal && (cards ? styles.cardsHorizontal : styles.horizontal))}>
          {props.options.map(option => <Radio key={option.value} option={option} name={name} group={props} cards={cards} failed={failed} chosen={chosen} pending={saving && asked === option.value} onPick={pick} />)}
        </div>
        {failed && <p id={errorId} role="alert" {...stylex.props(textStyle.label, styles.error)}>
            <WarningIcon />
            <span>{props.error}</span>
          </p>}
      </fieldset>
      {saving && <span role="status" {...stylex.props(visuallyHidden.hidden)}>
          Saving
        </span>}
    </>;
}
function Radio<Value extends string>({
  option,
  name,
  group,
  cards,
  failed,
  chosen,
  pending,
  onPick
}: {
  readonly option: RadioOption<Value>;
  readonly name: string;
  readonly group: Shared<Value>;
  readonly cards: boolean;
  readonly failed: boolean;
  readonly chosen: Value | null;
  /** This option was just chosen and the page is saving it. */
  readonly pending: boolean;
  readonly onPick: (value: Value) => void;
}) {
  const id = useId();
  const on = chosen === option.value;
  const off = group.disabled === true || option.disabled === true;
  const note = off && option.disabledReason !== undefined ? option.disabledReason : option.description;
  const descriptionId = note === undefined ? undefined : `${id}-description`;
  const busy = group.loading === true;
  const pick = onPick;
  const input = (extra: stylex.StyleXStyles, last: stylex.StyleXStyles = null) => <input id={id} type="radio" name={name} value={option.value} checked={on} disabled={off} aria-describedby={descriptionId} aria-disabled={busy || undefined} onChange={() => pick(option.value)}
  /* A radio already checked fires no change event, so choosing it again is heard as a click. */ onClick={on && group.manual === true ? () => pick(option.value) : undefined} onKeyDown={group.manual === true ? event => moveWithoutChoosing(event, value => {
    const picked = group.options.find(candidate => candidate.value === value);
    if (picked !== undefined) pick(picked.value);
  }) : undefined} data-testid={option.testId} {...stylex.props(choiceStyle.mark, extra, failed && !on && choiceStyle.invalid, off && choiceStyle.disabled, busy && choiceStyle.busy, last, focusRing)} />;
  if (cards) return <div className="x1n2onr6 xrvj5dj x1rkzygb x7a106z x1vvqtvb xp1t01x x3i8z2n x1nchb1j x9f619">
        {input([styles.cardInput, on && styles.cardInputOn, off && styles.cardInputDisabled])}
        {pending ? <span aria-hidden="true" className="x1n2onr6 x1z0q0p5 xq9ob8z x1h3jhxu x9f619 x1m3fsnl x1y0btm7 xdmng3o x47corl x78zum5 xc342km xjbqb8w x10dbofl">
            <Spinner />
          </span> : <span aria-hidden="true" {...stylex.props(styles.cardDot, on && styles.dotOn, off && choiceStyle.disabled)} />}
        <span className="x1n2onr6 x47corl x78zum5 xdt5ytf xeuugli xj0a0fe">
          <label htmlFor={id} {...stylex.props(textStyle.body, styles.cardLabel, off && styles.disabledText)}>
            {option.label}
          </label>
          {note !== undefined && <span id={descriptionId} {...stylex.props(textStyle.label, styles.cardDescription)}>
              {note}
            </span>}
        </span>
      </div>;
  return <div className="x78zum5 xdt5ytf x15xv0s5 xeuugli">
      <label htmlFor={id} {...stylex.props(textStyle.body, styles.row, off && styles.rowDisabled)}>
        <span className="x1n2onr6 x78zum5 x2lah0s x1z0q0p5 xq9ob8z x1h3jhxu">
          {input([styles.dot, on && styles.dotOn], off && on && choiceStyle.disabledOn)}
          {pending && <span aria-hidden="true" className="x10l6tqk x10a8y8t x78zum5 x10dbofl x40k5rp x1m3fsnl x47corl">
              <Spinner />
            </span>}
        </span>
        <span>{option.label}</span>
      </label>
      {note !== undefined && <span id={descriptionId} {...stylex.props(textStyle.label, styles.description)}>
          {note}
        </span>}
    </div>;
}